/* Alwan Dropdown: one open state for every single-select in the store.
   Design system: docs/design-system/components/Dropdown. Styles: Dropdown block in alwan-components.css.

   Each visible <select> (not multiple, not a list box) gets a combobox trigger and one shared list:
   a popover panel under the field on wider screens, a modal bottom sheet on phones.
   The real <select> stays in the form: it keeps its name, value, validation and events, and other
   scripts keep listening to it. Without this script the native select shows, styled the same.

   Keyboard (WAI-ARIA APG select-only combobox): Enter, Space, Alt+ArrowDown or ArrowUp/Down open;
   ArrowUp/Down, Home/End, PageUp/PageDown move; typing jumps to a matching option; Enter or Space
   selects; Escape closes without changing; Tab selects the active option and moves on.
   Opt out: data-alwan-native on the select (or an ancestor). */
(() => {
  if (window.AlwanDropdown) return;

  const SHEET_QUERY = window.matchMedia('(max-width: 767px)');
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SKIP = '[multiple], [size]:not([size="1"]), [hidden], [data-alwan-native] select, select[data-alwan-native], .selector-wrapper-hidden';
  const CHECK = '<svg class="alwan-dropdown__check" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M5 12l5 5l10 -10"/></svg>';
  const CLOSE = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M18 6l-12 12"/><path d="M6 6l12 12"/></svg>';
  const supportsPopover = typeof HTMLElement.prototype.showPopover === 'function';
  const nativeValue = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value');
  const nativeIndex = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'selectedIndex');
  const instances = new WeakMap();
  let uid = 0;
  let openInstance = null;

  const isArabic = () => (document.documentElement.lang || '').toLowerCase().startsWith('ar');
  const closeLabel = () => (window.theme && window.theme.strings && window.theme.strings.close) || (isArabic() ? 'إغلاق' : 'Close');
  const normalise = text => text.toLocaleLowerCase().replace(/[ً-ٰٟـ]/g, '').replace(/[إأآ]/g, 'ا').trim();
  const optionText = option => (option.label || option.textContent || '').replace(/\s+/g, ' ').trim();

  class AlwanDropdown {
    constructor(select) {
      this.select = select;
      this.id = `alwan-dropdown-${++uid}`;
      this.activeIndex = -1;
      this.typed = '';
      this.typedAt = 0;
      instances.set(select, this);
      this.build();
      this.watch();
      this.sync();
    }

    build() {
      const select = this.select;
      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'alwan-dropdown alwan-dropdown__trigger';
      trigger.id = `${this.id}-trigger`;
      trigger.setAttribute('role', 'combobox');
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', `${this.id}-list`);
      trigger.innerHTML = '<span class="alwan-dropdown__value"></span>';
      this.trigger = trigger;
      this.valueEl = trigger.firstElementChild;

      const list = document.createElement('div');
      list.className = 'alwan-dropdown__list';
      list.id = `${this.id}-list`;
      list.setAttribute('role', 'listbox');
      list.tabIndex = -1;
      this.list = list;

      const panel = document.createElement('div');
      panel.className = 'alwan-dropdown__panel';
      if (supportsPopover) panel.setAttribute('popover', 'manual');
      panel.hidden = !supportsPopover;
      this.panel = panel;

      const sheet = document.createElement('dialog');
      sheet.className = 'alwan-dropdown__sheet';
      sheet.setAttribute('aria-labelledby', `${this.id}-title`);
      sheet.innerHTML = `<div class="alwan-dropdown__sheet-header"><h2 class="alwan-dropdown__sheet-title" id="${this.id}-title"></h2><button type="button" class="alwan-dropdown__close">${CLOSE}<span class="alwan-visually-hidden"></span></button></div>`;
      this.sheet = sheet;
      this.sheetTitle = sheet.querySelector('.alwan-dropdown__sheet-title');
      sheet.querySelector('.alwan-visually-hidden').textContent = closeLabel();

      const extra = select.getAttribute('data-alwan-dropdown-class');
      if (extra) trigger.classList.add(...extra.split(/\s+/).filter(Boolean));
      select.classList.add('alwan-dropdown__native');
      select.tabIndex = -1;
      select.setAttribute('aria-hidden', 'true');
      select.dataset.alwanDropdown = 'ready';
      // Kept next to the field so drawers and forms still contain them; popover and dialog render in the top layer.
      // A wrapping <label> would forward clicks in the list to the select, so the list sits after it.
      select.insertAdjacentElement('afterend', trigger);
      (select.closest('label') || trigger).insertAdjacentElement('afterend', panel);
      panel.insertAdjacentElement('afterend', sheet);

      trigger.addEventListener('click', () => (this.isOpen() ? this.close(true) : this.open()));
      trigger.addEventListener('keydown', event => this.onKey(event));
      list.addEventListener('keydown', event => this.onKey(event));
      list.addEventListener('click', event => {
        const option = event.target.closest('[role="option"]');
        if (!option || option.getAttribute('aria-disabled') === 'true') return;
        this.choose(Number(option.dataset.index));
        this.close(true);
      });
      list.addEventListener('pointermove', event => {
        const option = event.target.closest('[role="option"]');
        if (option && option.getAttribute('aria-disabled') !== 'true') this.setActive(Number(option.dataset.index), false);
      });
      sheet.querySelector('.alwan-dropdown__close').addEventListener('click', () => this.close(true));
      sheet.addEventListener('cancel', event => { event.preventDefault(); this.close(true); });
      sheet.addEventListener('click', event => { if (event.target === sheet) this.close(true); });
      // Drawer scripts (filters, menus) trap Tab and Escape at the document; the sheet manages its own.
      sheet.addEventListener('keydown', event => {
        event.stopPropagation();
        if (event.key !== 'Tab') return;
        event.preventDefault();
        const close = sheet.querySelector('.alwan-dropdown__close');
        (document.activeElement === close ? this.list : close).focus();
      });
      for (const layer of [panel, sheet]) layer.addEventListener('click', event => event.stopPropagation());
      select.addEventListener('focus', () => trigger.focus());
      select.addEventListener('invalid', () => trigger.setAttribute('aria-invalid', 'true'));
      select.addEventListener('change', () => this.sync());
      select.addEventListener('input', () => this.sync());
      for (const label of select.labels || []) {
        label.addEventListener('click', event => { event.preventDefault(); trigger.focus(); });
      }
    }

    watch() {
      const select = this.select;
      const instance = this;
      Object.defineProperty(select, 'value', {
        configurable: true,
        get() { return nativeValue.get.call(this); },
        set(value) { nativeValue.set.call(this, value); instance.sync(); }
      });
      Object.defineProperty(select, 'selectedIndex', {
        configurable: true,
        get() { return nativeIndex.get.call(this); },
        set(value) { nativeIndex.set.call(this, value); instance.sync(); }
      });
      this.observer = new MutationObserver(() => {
        if (!select.isConnected) return this.destroy();
        this.sync();
      });
      this.observer.observe(select, {attributes: true, attributeFilter: ['disabled', 'required', 'aria-invalid', 'aria-describedby', 'aria-label', 'class'], childList: true, subtree: true, characterData: true});
      this.optionObserver = new MutationObserver(() => this.sync());
      this.optionObserver.observe(select, {attributes: true, subtree: true, attributeFilter: ['selected', 'disabled', 'label']});
    }

    labelText() {
      const labels = [...(this.select.labels || [])];
      const text = labels.map(label => {
        const copy = label.cloneNode(true);
        copy.querySelectorAll('select, button, dialog, [popover], .alwan-dropdown__panel').forEach(node => node.remove());
        return copy.textContent.replace(/\s+/g, ' ').trim();
      }).filter(Boolean).join(' ');
      return text || this.select.getAttribute('aria-label') || this.select.title || '';
    }

    sync() {
      const {select, trigger} = this;
      // A label that wraps the field would name it with every option, so name it from the label's own text.
      const labels = [...(select.labels || [])].filter(label => !label.contains(select));
      labels.forEach((label, index) => { if (!label.id) label.id = `${this.id}-label-${index}`; });
      if (labels.length) {
        trigger.setAttribute('aria-labelledby', labels.map(label => label.id).join(' '));
        trigger.removeAttribute('aria-label');
      } else {
        trigger.removeAttribute('aria-labelledby');
        const name = this.labelText();
        if (name) trigger.setAttribute('aria-label', name); else trigger.removeAttribute('aria-label');
      }
      for (const name of ['aria-describedby', 'aria-required']) {
        const value = select.getAttribute(name);
        if (value) trigger.setAttribute(name, value); else trigger.removeAttribute(name);
      }
      if (select.required) trigger.setAttribute('aria-required', 'true');
      const invalid = select.getAttribute('aria-invalid');
      if (invalid) trigger.setAttribute('aria-invalid', invalid); else if (select.checkValidity()) trigger.removeAttribute('aria-invalid');
      trigger.disabled = select.disabled;
      const selected = select.options[nativeIndex.get.call(select)];
      this.valueEl.textContent = selected ? optionText(selected) : '';
      this.sheetTitle.textContent = this.labelText() || this.valueEl.textContent;
      if (this.isOpen()) this.render();
    }

    render() {
      const {select, list} = this;
      const current = nativeIndex.get.call(select);
      const fragment = document.createDocumentFragment();
      let group = null;
      [...select.options].forEach((option, index) => {
        if (option.hidden) return;
        const parent = option.parentElement;
        if (parent && parent.tagName === 'OPTGROUP') {
          if (!group || group.dataset.source !== String([...select.children].indexOf(parent))) {
            group = document.createElement('div');
            group.setAttribute('role', 'group');
            group.dataset.source = String([...select.children].indexOf(parent));
            const heading = document.createElement('div');
            heading.className = 'alwan-dropdown__group';
            heading.id = `${this.id}-group-${group.dataset.source}`;
            heading.textContent = parent.label;
            group.setAttribute('aria-labelledby', heading.id);
            group.append(heading);
            fragment.append(group);
          }
        } else {
          group = null;
        }
        const row = document.createElement('div');
        row.className = 'alwan-dropdown__option';
        row.id = `${this.id}-option-${index}`;
        row.dataset.index = String(index);
        row.setAttribute('role', 'option');
        row.setAttribute('aria-selected', String(index === current));
        if (option.disabled || (parent && parent.disabled)) row.setAttribute('aria-disabled', 'true');
        const text = document.createElement('span');
        text.className = 'alwan-dropdown__option-text';
        text.textContent = optionText(option);
        row.append(text);
        row.insertAdjacentHTML('beforeend', CHECK);
        (group || fragment).append(row);
      });
      list.replaceChildren(fragment);
      const labelledBy = this.trigger.getAttribute('aria-labelledby');
      if (labelledBy) { list.setAttribute('aria-labelledby', labelledBy); list.removeAttribute('aria-label'); }
      else { list.removeAttribute('aria-labelledby'); list.setAttribute('aria-label', this.labelText()); }
      if (this.activeIndex >= 0) this.setActive(this.activeIndex, false);
    }

    rows() {
      return [...this.list.querySelectorAll('[role="option"]:not([aria-disabled="true"])')];
    }

    setActive(index, scroll = true) {
      this.activeIndex = index;
      const focusHolder = this.mode === 'sheet' ? this.list : this.trigger;
      const other = this.mode === 'sheet' ? this.trigger : this.list;
      other.removeAttribute('aria-activedescendant');
      this.list.querySelectorAll('.is-active').forEach(row => row.classList.remove('is-active'));
      const row = this.list.querySelector(`[data-index="${index}"]`);
      if (!row) { focusHolder.removeAttribute('aria-activedescendant'); return; }
      row.classList.add('is-active');
      focusHolder.setAttribute('aria-activedescendant', row.id);
      if (scroll) row.scrollIntoView({block: 'nearest'});
    }

    move(step) {
      const rows = this.rows();
      if (!rows.length) return;
      const position = rows.findIndex(row => Number(row.dataset.index) === this.activeIndex);
      let next;
      if (step === 'first') next = 0;
      else if (step === 'last') next = rows.length - 1;
      else next = Math.min(rows.length - 1, Math.max(0, (position < 0 ? 0 : position) + step));
      this.setActive(Number(rows[next].dataset.index));
    }

    typeahead(key) {
      const now = Date.now();
      this.typed = now - this.typedAt > 600 ? key : this.typed + key;
      this.typedAt = now;
      const query = normalise(this.typed);
      const options = [...this.select.options];
      const rows = this.rows().map(row => Number(row.dataset.index));
      const candidates = this.isOpen() ? rows : options.map((option, index) => index).filter(index => !options[index].disabled && !options[index].hidden);
      const start = Math.max(0, candidates.indexOf(this.isOpen() ? this.activeIndex : nativeIndex.get.call(this.select)));
      const ordered = this.typed.length === 1 ? [...candidates.slice(start + 1), ...candidates.slice(0, start + 1)] : [...candidates.slice(start), ...candidates.slice(0, start)];
      const match = ordered.find(index => normalise(optionText(options[index])).startsWith(query));
      if (match === undefined) return;
      if (this.isOpen()) this.setActive(match);
      else this.choose(match);
    }

    onKey(event) {
      if (this.trigger.disabled) return;
      const open = this.isOpen();
      const {key} = event;
      if (!open) {
        if (['ArrowDown', 'ArrowUp', 'Enter', ' ', 'Home', 'End'].includes(key)) {
          event.preventDefault();
          this.open(key === 'Home' ? 'first' : key === 'End' ? 'last' : 'current');
        } else if (key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          event.preventDefault();
          this.typeahead(key);
        }
        return;
      }
      switch (key) {
        case 'ArrowDown': event.preventDefault(); this.move(1); break;
        case 'ArrowUp':
          event.preventDefault();
          if (event.altKey) { this.choose(this.activeIndex); this.close(true); } else this.move(-1);
          break;
        case 'Home': event.preventDefault(); this.move('first'); break;
        case 'End': event.preventDefault(); this.move('last'); break;
        case 'PageDown': event.preventDefault(); this.move(10); break;
        case 'PageUp': event.preventDefault(); this.move(-10); break;
        case 'Enter':
          event.preventDefault(); this.choose(this.activeIndex); this.close(true); break;
        case ' ':
          event.preventDefault();
          if (this.typed && Date.now() - this.typedAt < 600) this.typeahead(' ');
          else { this.choose(this.activeIndex); this.close(true); }
          break;
        case 'Escape': event.preventDefault(); event.stopPropagation(); this.close(true); break;
        case 'Tab':
          if (this.mode === 'sheet') break;
          this.choose(this.activeIndex); this.close(false); break;
        default:
          if (key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) { event.preventDefault(); this.typeahead(key); }
      }
    }

    choose(index) {
      const select = this.select;
      const option = select.options[index];
      if (!option || option.disabled) return;
      if (index === nativeIndex.get.call(select)) return;
      nativeIndex.set.call(select, index);
      this.sync();
      select.dispatchEvent(new Event('input', {bubbles: true}));
      select.dispatchEvent(new Event('change', {bubbles: true}));
    }

    isOpen() {
      return this.trigger.getAttribute('aria-expanded') === 'true';
    }

    open(start = 'current') {
      if (this.trigger.disabled || this.isOpen()) return;
      if (openInstance && openInstance !== this) openInstance.close(false);
      openInstance = this;
      this.mode = SHEET_QUERY.matches ? 'sheet' : 'panel';
      this.render();
      const rows = this.rows();
      const current = nativeIndex.get.call(this.select);
      const currentRow = rows.find(row => Number(row.dataset.index) === current);
      this.trigger.setAttribute('aria-expanded', 'true');
      if (this.mode === 'sheet') {
        this.sheet.append(this.list);
        this.sheet.classList.toggle('is-instant', REDUCED_MOTION.matches);
        this.sheet.showModal();
        this.list.focus({preventScroll: true});
      } else {
        this.panel.append(this.list);
        if (supportsPopover) this.panel.showPopover(); else this.panel.hidden = false;
        this.place();
        this.onReflow = () => this.place();
        this.onOutside = event => {
          if (!this.panel.contains(event.target) && !this.trigger.contains(event.target)) this.close(false);
        };
        window.addEventListener('resize', this.onReflow);
        window.addEventListener('scroll', this.onReflow, {passive: true, capture: true});
        document.addEventListener('pointerdown', this.onOutside, true);
      }
      const target = start === 'first' ? rows[0] : start === 'last' ? rows.at(-1) : currentRow || rows[0];
      if (target) this.setActive(Number(target.dataset.index));
    }

    close(returnFocus) {
      if (!this.isOpen()) return;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.removeAttribute('aria-activedescendant');
      this.list.removeAttribute('aria-activedescendant');
      if (this.mode === 'sheet') {
        if (this.sheet.open) this.sheet.close();
      } else {
        if (supportsPopover) { if (this.panel.matches(':popover-open')) this.panel.hidePopover(); } else this.panel.hidden = true;
        window.removeEventListener('resize', this.onReflow);
        window.removeEventListener('scroll', this.onReflow, {capture: true});
        document.removeEventListener('pointerdown', this.onOutside, true);
      }
      this.activeIndex = -1;
      if (openInstance === this) openInstance = null;
      if (returnFocus) this.trigger.focus({preventScroll: true});
    }

    place() {
      const rect = this.trigger.getBoundingClientRect();
      if (!rect.width) return this.close(false);
      const panel = this.panel;
      const gap = 4;
      const margin = 16;
      const rtl = getComputedStyle(this.trigger).direction === 'rtl';
      panel.style.minWidth = `${Math.round(rect.width)}px`;
      panel.style.maxWidth = `${window.innerWidth - margin * 2}px`;
      const below = window.innerHeight - rect.bottom - gap - margin;
      const above = rect.top - gap - margin;
      const wanted = Math.min(360, this.list.scrollHeight + 10);
      const flip = below < Math.min(wanted, 200) && above > below;
      panel.style.maxHeight = `${Math.max(120, Math.min(360, flip ? above : below))}px`;
      panel.style.top = flip ? 'auto' : `${Math.round(rect.bottom + gap)}px`;
      panel.style.bottom = flip ? `${Math.round(window.innerHeight - rect.top + gap)}px` : 'auto';
      const width = panel.offsetWidth;
      let start = rtl ? rect.right - width : rect.left;
      start = Math.min(Math.max(margin, start), window.innerWidth - margin - width);
      panel.style.left = `${Math.round(start)}px`;
      panel.style.right = 'auto';
    }

    destroy() {
      this.close(false);
      this.observer.disconnect();
      this.optionObserver.disconnect();
      this.trigger.remove();
      this.panel.remove();
      this.sheet.remove();
      instances.delete(this.select);
    }
  }

  // After a choice re-renders its section (sorting, filtering), keep focus on the same field.
  let refocus = null;
  document.addEventListener('change', event => {
    if (event.target.matches && event.target.matches('select[data-alwan-dropdown="ready"]') && event.target.id) refocus = {id: event.target.id, at: Date.now()};
  }, true);
  const restoreFocus = instance => {
    if (!refocus || refocus.id !== instance.select.id || Date.now() - refocus.at > 5000) return;
    const active = document.activeElement;
    if (active && active !== document.body && active.isConnected) return;
    refocus = null;
    instance.trigger.focus({preventScroll: true});
  };
  const eligible = select => !select.matches(SKIP) && !select.closest('noscript, [data-alwan-native]') && !instances.has(select) && getComputedStyle(select).display !== 'none';
  const enhance = (root = document) => {
    if (root.nodeType !== 1 && root !== document) return;
    const selects = root.matches && root.matches('select') ? [root] : root.querySelectorAll('select');
    selects.forEach(select => { if (eligible(select)) restoreFocus(new AlwanDropdown(select)); });
  };

  window.AlwanDropdown = {enhance, get: select => instances.get(select)};
  enhance();
  new MutationObserver(mutations => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach(node => { if (node.nodeType === 1) enhance(node); });
      mutation.removedNodes.forEach(node => {
        if (node.nodeType !== 1) return;
        const selects = node.matches('select') ? [node] : node.querySelectorAll('select');
        selects.forEach(select => { const instance = instances.get(select); if (instance && !select.isConnected) instance.destroy(); });
      });
    }
  }).observe(document.body, {childList: true, subtree: true});
  window.addEventListener('pageshow', () => document.querySelectorAll('select[data-alwan-dropdown="ready"]').forEach(select => instances.get(select)?.sync()));
  SHEET_QUERY.addEventListener('change', () => openInstance && openInstance.close(false));
})();
